import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';

import { meQuery } from '@libs/graphql';
import { convertExponentialToDecimal } from '@libs/helpers';
import { TransactionAmountType } from '@libs/backend-common';
import { CurrencyType, ResultFeedbackType } from '@libs/types';
import { Button, LongArrowIcon, Typography } from '@libs/components';
import { formatTimerDate, useApiResponse, useCounter } from '@libs/hooks';

import {
  exchangeCurrencyMutation,
  ExchangeCurrencyResponse,
  getUserTransactionsCountQuery,
} from 'src/gql';
import { Wallet } from 'src/assets';
import { TradeType } from 'src/constants';
import { useTheme } from 'styled-components';
import { GTMEventNames, TradeFormNew } from 'src/types';
import { getAlternativeAssetLabel, pushGTMEvent } from 'src/helpers';
import { useTranslation, useAssets, useRequestExchange } from 'src/hooks';

import {
  AssetsContainer,
  Container,
  Full,
  DetailsWrapper,
  TextWithProgress,
  DetailRow,
  ProgressBarWrapper,
  ProgressBar,
  BorderBox,
  Separator,
  IconContainer,
  IconWrapper,
  ConfirmSectionWrapper,
  CustomImg,
  SeparatorThin,
  WalletWrapper,
  InfoWrapper,
  CustomAssetRow,
  CustomTypography,
} from './styles';

type ConvertDetailsProps = {
  form: TradeFormNew;
  fromAssetCode: CurrencyType;
  toAssetCode: CurrencyType;
  convertType: TradeType;
  isConvert?: boolean;
  firstButtonHandler: () => void;
  secondButtonHandler: () => void;
  shouldShowMethodInfo?: boolean;
  fromPlaceHolder?: string;
  toPlaceHolder?: string;
};
const MINIMUM_COUNTDOWN_SECONDS = 10;

export const ConvertDetails: React.FC<ConvertDetailsProps> = ({
  form,
  fromAssetCode,
  toAssetCode,
  isConvert,
  firstButtonHandler,
  secondButtonHandler,
  shouldShowMethodInfo = false,
  fromPlaceHolder,
  toPlaceHolder,
}) => {
  const { t } = useTranslation();
  const { assets } = useAssets();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [stopRequestExchange, setStopRequestExchange] = useState(false);
  const { requestExchange, exchangeData, preReqLoading } = useRequestExchange();

  const rate = exchangeData?.rate;
  const expiresIn = exchangeData?.expiresIn;
  const toAmount = exchangeData?.receivedAmount;
  const fromAmount = exchangeData?.neededAmount;
  const transactionFee = exchangeData?.feeAmount;
  const feeInPercent =
    Number(exchangeData?.feeConditions?.exchangeFee?.buyRate) * 100;

  const { counterInterval, isCounterRunning, clearCurrentInterval, countDown } =
    useCounter(0);

  useEffect(() => {
    if (countDown === 0) clearCurrentInterval();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countDown]);
  useEffect(() => {
    if (exchangeData?.expiresIn) {
      clearCurrentInterval();
      counterInterval(exchangeData.expiresIn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeData]);

  const createExchangePreRequest = () => {
    if (!fromAssetCode || !toAssetCode || !form.type) return;

    const isBaseAssetValueDot =
      form.fromAssetAmount[form.fromAssetAmount.length - 1] === '.';
    const isQuoteAssetValueDot =
      form.toAssetAmount[form.toAssetAmount.length - 1] === '.';

    const baseAssetValueDotReplace = +form.fromAssetAmount?.replace?.('.', '');
    const quoteAssetValueDotReplace = +form.toAssetAmount?.replace?.('.', '');

    const fromAssetAmount = isBaseAssetValueDot
      ? baseAssetValueDotReplace
      : +form.fromAssetAmount;
    const toAssetAmount = isQuoteAssetValueDot
      ? quoteAssetValueDotReplace
      : +form.toAssetAmount;

    const amount =
      form.type === TransactionAmountType.GROSS
        ? fromAssetAmount
        : toAssetAmount;

    requestExchange({
      variables: {
        record: {
          amount,
          fromAssetCode: fromAssetCode,
          type: form.type,
          toAssetCode: toAssetCode,
        },
      },
    });
  };

  const [handleExchange, res] = useMutation(exchangeCurrencyMutation);

  const convertCurrency = () => {
    if (!exchangeData?.id) return;
    clearCurrentInterval();
    setStopRequestExchange(true);

    handleExchange({
      variables: {
        record: {
          requestId: +exchangeData.id,
        },
      },
    });
  };

  const { fromCurrency, toCurrency } = useMemo(() => {
    let fromCurr, toCurr;
    for (const asset of assets.allAssets) {
      if (asset.code === fromAssetCode) {
        fromCurr = asset;
      } else if (asset.code === toAssetCode) {
        toCurr = asset;
      }
      if (fromCurr && toCurr)
        return {
          fromCurrency: fromCurr,
          toCurrency: toCurr,
        };
    }

    return {
      fromCurrency: fromCurr,
      toCurrency: toCurr,
    };
  }, [assets.allAssets, fromAssetCode, toAssetCode]);

  const fromCurrencyLabel = getAlternativeAssetLabel(
    fromCurrency?.code as CurrencyType
  );
  const toCurrencyLabel = getAlternativeAssetLabel(
    toCurrency?.code as CurrencyType
  );
  const commissionRowDescription =
    convertExponentialToDecimal(transactionFee || 0) + ' ' + fromCurrencyLabel;

  const meRes = useQuery(meQuery);
  const { response } = useApiResponse(meRes);
  const transactionRes = useQuery(getUserTransactionsCountQuery, {
    variables: { record: {} },
  });
  const { response: transactionsCount } = useApiResponse(transactionRes);

  const pushSuccessConversionEvent = () => {
    const userTotalTransaction =
      transactionsCount?.getUserTransactionsCount || 0;

    pushGTMEvent(GTMEventNames.cryptoTransaction, {
      user_id: response?.me.id,
      transactio_total: userTotalTransaction + 1,
      // transaction_type: tradeType,
    });
  };

  const handleSuccessConversion = (data: ExchangeCurrencyResponse) => {
    setStopRequestExchange(false);
    clearCurrentInterval();
    pushSuccessConversionEvent();

    const convertDescription = t('convert_success_text', {
      amount: (toAmount || 0).toString(),
      assetCode: getAlternativeAssetLabel(toCurrency?.code as CurrencyType),
    });
    const description =
      data.exchange === ResultFeedbackType.SUCCESS
        ? t('trade_success_desc', {
            fromAmount: (fromAmount || 0).toString(),
            toAmount: (toAmount || 0).toString(),
            fromCurrency: getAlternativeAssetLabel(
              fromCurrency?.code as CurrencyType
            ),
            toCurrency: getAlternativeAssetLabel(
              toCurrency?.code as CurrencyType
            ),
          })
        : t('withdraw_status', {
            amount: toAmount || '',
            currency: toCurrency?.code
              ? getAlternativeAssetLabel(toCurrency?.code as CurrencyType)
              : '',
          });

    navigate('/feedback', {
      replace: true,
      state: {
        type: ResultFeedbackType[data.exchange],
        navigationPath: '/',
        description: (
          <Typography fontFamily="primaryMedium" color={colors.text.secondary}>
            {isConvert ? convertDescription : description}
          </Typography>
        ),
        buttonText: t('common_dashboard'),
      },
    });
  };

  const handleErrorConversion = () => {
    setStopRequestExchange(false);

    clearCurrentInterval();

    navigate('/feedback', {
      replace: true,
      state: {
        type: ResultFeedbackType.ERROR,
        navigationPath: '/',
        buttonText: t('common_dashboard'),
      },
    });
  };
  const { loading: conversionLoading } = useApiResponse(
    res,
    handleSuccessConversion,
    handleErrorConversion
  );
  const isExpired = !isCounterRunning && !conversionLoading;
  const buttonLoading = preReqLoading || conversionLoading;

  useEffect(() => {
    if (isExpired && !stopRequestExchange) {
      createExchangePreRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpired, stopRequestExchange]);

  const { minutes, seconds } = formatTimerDate(countDown);
  const countDownStr = `${minutes}:${
    +seconds < MINIMUM_COUNTDOWN_SECONDS && +seconds !== 0
      ? `0${seconds}`
      : seconds
  }`;

  const styles = buildStyles({
    trailColor: colors.borders.default,
    pathColor: colors.text.default,
  });

  const fromCurrencyCode = fromCurrency?.code
    ? getAlternativeAssetLabel(fromCurrency?.code as CurrencyType)
    : '';

  return (
    <>
      <Container>
        <BorderBox>
          <AssetsContainer>
            <CustomAssetRow
              amount={fromAmount}
              onChange={firstButtonHandler}
              label={
                fromPlaceHolder ? fromPlaceHolder : t('common_asset_from') + ':'
              }
              icon={fromCurrency?.iconUrl}
            />

            <Separator>
              <IconContainer>
                <IconWrapper>
                  <LongArrowIcon />
                </IconWrapper>
              </IconContainer>
            </Separator>

            <CustomAssetRow
              amount={toAmount}
              onChange={secondButtonHandler}
              label={toPlaceHolder ? toPlaceHolder : t('common_asset_to') + ':'}
              icon={toCurrency?.iconUrl}
            />
          </AssetsContainer>

          <DetailsWrapper>
            <DetailRow>
              <TextWithProgress>
                <Typography
                  type="bodyButton"
                  fontWeight={500}
                  fontFamily="primaryMedium"
                >
                  {t('trade_rate')}
                  {': '}
                </Typography>
                {expiresIn && (
                  <ProgressBarWrapper>
                    <ProgressBar>
                      <CircularProgressbar
                        styles={styles}
                        strokeWidth={15}
                        value={countDown}
                        maxValue={exchangeData.expiresIn}
                        counterClockwise
                      />
                    </ProgressBar>
                    <Typography
                      type="caption"
                      fontWeight={500}
                      fontFamily="primaryMedium"
                    >
                      {' '}
                      {countDownStr}
                    </Typography>
                  </ProgressBarWrapper>
                )}
              </TextWithProgress>
              <Typography type="bodyButton" fontFamily="primaryMedium">
                1 {toCurrencyLabel} ≈ {rate} {fromCurrencyLabel}
              </Typography>
            </DetailRow>
            <SeparatorThin />
            <DetailRow>
              <Typography
                type="bodyButton"
                fontFamily="primaryMedium"
                color={colors.text.secondary}
              >
                {t('trade_transaction_fees_with_percent', {
                  fee: feeInPercent,
                })}
                :
              </Typography>
              <Typography type="bodyButton" fontFamily="primaryBold">
                {commissionRowDescription}
              </Typography>
            </DetailRow>
          </DetailsWrapper>
        </BorderBox>
        <Full />

        <ConfirmSectionWrapper>
          {shouldShowMethodInfo && (
            <>
              <CustomTypography type="bodyButton" color={colors.text.secondary}>
                {t('payment_method')}
              </CustomTypography>

              <SeparatorThin />

              <WalletWrapper>
                <CustomImg src={Wallet} />
                <InfoWrapper>
                  <Typography type="subHeadline" fontFamily="primaryBold">
                    {t('buy_with_balance_title')}
                  </Typography>
                  <Typography
                    color={colors.text.secondary}
                    type="caption"
                    fontFamily="primaryMedium"
                  >{`${fromCurrency?.name} (${fromCurrencyCode})`}</Typography>
                </InfoWrapper>
              </WalletWrapper>
            </>
          )}

          <Button
            title={t('common_confirm')}
            size="lg"
            onClick={convertCurrency}
            disabled={buttonLoading}
            isLoading={buttonLoading}
          />
        </ConfirmSectionWrapper>
      </Container>
    </>
  );
};
export default ConvertDetails;
